import { TextInputProps } from 'components/forms/TextInput';
import {
    FocusEventHandler,
    ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import * as React from 'react';
import styled, { css } from 'styled-components';
import theme from 'styled-theming';
import {
    disabledText,
    inputBorder,
    inputBorderFocused,
    inputBorderInvalid,
    inputBorderValid,
    primaryText,
    secondaryText,
} from 'styles/theme';
import { TimeSpan } from 'utils/TimeSpan';

export type DurationInputProps = {
    /**
     * Uses aria-invalid to mark the field as invalid and be styled as such.
     */
    invalid?: boolean;
    /**
     * Whether or not the text input can be edited.
     */
    readonly?: boolean;
    /**
     * Whether or not to show a green underline when the input is not invalid. Defaults to `false`.
     */
    showValidState?: boolean;

    className?: string;

    disabled?: boolean;

    showDays?: boolean;
    showSeconds?: boolean;

    unitLabels?: Record<Units, ReactNode>;

    value?: TimeSpan;
    onChange?: (newValue: TimeSpan) => void;
};

type Units = 'days' | 'hours' | 'minutes' | 'seconds';

/**
 * The default padding to provide around the text inside the input.
 */
export const defaultInputPadding = '1rem';

const dontPassProps = ['showValidState', 'padding', 'width'];

const Input = styled.input.withConfig({
    shouldForwardProp: (p) => !dontPassProps.includes(p),
})<TextInputProps>`
    box-sizing: border-box;
    padding: ${({ padding }) => padding ?? defaultInputPadding} 0;
    ${({ width }) => (width ? `width: ${width};` : '')}
    border: 0;
    border-bottom: 1px solid ${inputBorder};
    background: transparent;
    color: ${primaryText};
    outline: none; /* remove default focus outline since we have our own focus state */

    /* [1]: prevent layout shift of larger border width when focused / invalid */
    padding-bottom: calc(${({ padding }) => padding ?? defaultInputPadding} + 1px);

    &:focus {
        border-color: ${inputBorderFocused};
        border-width: 2px;
        padding-bottom: ${({ padding }) =>
            padding ?? defaultInputPadding}; /* see comment [1] */
    }

    &:disabled {
        color: ${disabledText};
    }

    &::placeholder {
        color: ${secondaryText};
    }

    &[aria-invalid='true'] {
        border-color: ${inputBorderInvalid};
        border-width: 2px;
        padding-bottom: ${({ padding }) =>
            padding ?? defaultInputPadding}; /* see comment [1] */
    }

    ${({ padding, showValidState }) =>
        showValidState &&
        css`
            &[aria-invalid='false'] {
                border-color: ${inputBorderValid};
                border-width: 2px;
                padding-bottom: ${() =>
                    padding ?? defaultInputPadding}; /* see comment [1] */
            }
        `}
`;

// const TimeDurationRegex = /^(\d{2}):(\d{2})(?::(\d{2}(?:\.\d{0,7})?))?$/;
const valWithOneMoreDigit = (val: number) => val * 10; // if the user typed another 0

const setWithConstraints = (
    setter: (newVal: number) => void,
    min: number,
    max: number,
    shouldRound: boolean,
    nextElement: React.RefObject<HTMLInputElement> | undefined
) => (newVal: number, eventSource: EventSource) => {
    const valToSet = shouldRound ? Math.round(newVal) : newVal;
    if (Number.isNaN(valToSet) || valToSet < min) {
        setter(min);
        return min;
    }
    if (valToSet > max) {
        if (eventSource === 'keypress' && nextElement) {
            nextElement.current?.focus();
        } else {
            setter(max);
        }

        return max;
    }

    if (
        eventSource === 'keypress' &&
        valWithOneMoreDigit(valToSet) > max &&
        nextElement
    ) {
        nextElement.current?.focus();
    }

    setter(valToSet);
    return valToSet;
};

export const renderTimeNumber = (val: number) =>
    Number.isNaN(val) ? '00' : val.toFixed(0).padStart(2, '0');

const dhmsToTimeSpan = (days: number, hours: number, minutes: number, seconds: number) =>
    TimeSpan.fromMillis(
        1000 * (days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60 + seconds)
    );

const selectAllOnFocus: FocusEventHandler<HTMLInputElement> = (e) => {
    e.target.select();
};
const inputWidth = '4ch';

type EventSource = 'keypress' | 'other';

export function DurationInput({
    invalid,
    readonly = false,
    disabled = false,
    showValidState = false,
    showDays = false,
    showSeconds = false,
    className,
    value,
    onChange,
    unitLabels = {
        days: <span className="mr-10">d</span>,
        hours: <span className="mr-10">h</span>,
        minutes: <span className="mr-10">m</span>,
        seconds: <span className="mr-10">s</span>,
    },
}: DurationInputProps) {
    const eventSource = useRef<EventSource>('other');

    const daysInputRef = useRef<HTMLInputElement>(null);
    const hoursInputRef = useRef<HTMLInputElement>(null);
    const minutesInputRef = useRef<HTMLInputElement>(null);
    const secondsInputRef = useRef<HTMLInputElement>(null);

    const [days, _setDays] = useState(0);
    const [hours, _setHours] = useState(0);
    const [minutes, _setMinutes] = useState(0);
    const [seconds, _setSeconds] = useState(0);

    const setDays = useMemo(
        () => setWithConstraints(_setDays, 0, 365 * 10, true, hoursInputRef),
        []
    );
    const setHours = useMemo(
        () =>
            setWithConstraints(
                _setHours,
                0,
                24, // TODO: minutes + seconds > 0 ? 23 : 24 or similar
                true,
                minutesInputRef
            ),
        []
    );
    const setMinutes = useMemo(
        () => setWithConstraints(_setMinutes, 0, 59, true, secondsInputRef),
        []
    );
    const setSeconds = useMemo(
        () => setWithConstraints(_setSeconds, 0, 59, false, undefined),
        []
    );

    useEffect(() => {
        if (value) {
            if (showDays) {
                _setDays(value.days);
                _setHours(value.hours);
            } else {
                _setDays(0);
                _setHours(value.hours || value.days * 24);
            }
            _setMinutes(value.minutes);
            _setSeconds(value.seconds);
        } else {
            _setDays(0);
            _setHours(0);
            _setMinutes(0);
            _setSeconds(0);
        }
    }, [showDays, value]);

    return (
        <DurationInputContainer
            className={className}
            $themeColor={disabled ? disabledText : primaryText}
        >
            {showDays && (
                <>
                    <Input
                        type="number"
                        value={days}
                        onKeyPress={() => {
                            eventSource.current = 'keypress';
                        }}
                        onChange={(e) => {
                            const newDays = setDays(
                                e.target.valueAsNumber,
                                eventSource.current
                            );
                            onChange?.(dhmsToTimeSpan(newDays, hours, minutes, seconds));
                            eventSource.current = 'other';
                        }}
                        width={inputWidth}
                        readOnly={readonly}
                        ref={daysInputRef}
                        onFocus={selectAllOnFocus}
                        showValidState={showValidState}
                        invalid={invalid}
                        disabled={disabled}
                    />
                    {unitLabels.days}
                </>
            )}
            <Input
                type="number"
                value={renderTimeNumber(hours)}
                onKeyPress={() => {
                    eventSource.current = 'keypress';
                }}
                onChange={(e) => {
                    const newHours = setHours(
                        e.target.valueAsNumber,
                        eventSource.current
                    );
                    onChange?.(dhmsToTimeSpan(days, newHours, minutes, seconds));
                    eventSource.current = 'other';
                }}
                width={inputWidth}
                readOnly={readonly}
                ref={hoursInputRef}
                onFocus={selectAllOnFocus}
                showValidState={showValidState}
                invalid={invalid}
                disabled={disabled}
            />
            {unitLabels.hours}
            <Input
                type="number"
                value={renderTimeNumber(minutes)}
                onKeyPress={() => {
                    eventSource.current = 'keypress';
                }}
                onChange={(e) => {
                    const newMinutes = setMinutes(
                        e.target.valueAsNumber,
                        eventSource.current
                    );
                    onChange?.(dhmsToTimeSpan(days, hours, newMinutes, seconds));
                    eventSource.current = 'other';
                }}
                width={inputWidth}
                readOnly={readonly}
                ref={minutesInputRef}
                onFocus={selectAllOnFocus}
                showValidState={showValidState}
                invalid={invalid}
                disabled={disabled}
            />
            {unitLabels.minutes}
            {showSeconds && (
                <>
                    <Input
                        type="number"
                        value={renderTimeNumber(seconds)}
                        onKeyPress={() => {
                            eventSource.current = 'keypress';
                        }}
                        onChange={(e) => {
                            const newSeconds = setSeconds(
                                e.target.valueAsNumber,
                                eventSource.current
                            );
                            onChange?.(dhmsToTimeSpan(days, hours, minutes, newSeconds));
                            eventSource.current = 'other';
                        }}
                        width={inputWidth}
                        readOnly={readonly}
                        ref={secondsInputRef}
                        onFocus={selectAllOnFocus}
                        showValidState={showValidState}
                        invalid={invalid}
                        disabled={disabled}
                    />
                    {unitLabels.seconds}
                </>
            )}
        </DurationInputContainer>
    );
}

const DurationInputContainer = styled.div<Readonly<{ $themeColor: theme.ThemeSet }>>`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 2px;
    color: ${({ $themeColor: color }) => color};
`;
